import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { BRAND, mailtoLink } from "@/data/site";

const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl md:text-3xl text-ink mt-14 mb-5 leading-tight">
    {children}
  </h2>
);

const UL = ({ children }) => (
  <ul className="list-disc list-outside pl-6 marker:text-gold space-y-2 mb-4 text-ink/75 text-[15px] leading-relaxed">
    {children}
  </ul>
);

const P = ({ children }) => (
  <p className="text-ink/75 leading-relaxed mb-4 text-[15px]">{children}</p>
);

const VerhuurvoorwaardenMagicMirror = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-40 md:pt-48 pb-14 bg-beige print-hide">
        <div className="container-x text-center max-w-3xl mx-auto">
          <p className="eyebrow mb-4">Verhuur</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink">
            Verhuurvoorwaarden <span className="italic text-gold">Magic Mirror</span> Fotobooth
          </h1>
          <p className="mt-6 text-sm text-ink/60 tracking-wider">
            Lenslopers Fotografie — Laatst bijgewerkt: 12 augustus 2026
          </p>
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="verhuurvoorwaarden-print-btn"
            className="btn-outline-dark mt-9 gap-3"
          >
            <Printer size={15} strokeWidth={1.6} />
            Download / Print als PDF
          </button>
        </div>
      </section>

      {/* Print-only document header */}
      <div className="print-only mb-8">
        <p className="font-serif text-2xl text-ink">
          Verhuurvoorwaarden Magic Mirror Fotobooth
        </p>
        <p className="text-sm text-ink/60 mt-1">
          Lenslopers Fotografie — www.lenslopers.com — Laatst bijgewerkt: 12 augustus 2026
        </p>
        <div className="h-px bg-gold mt-4" />
      </div>

      {/* Content */}
      <section className="bg-white py-16 md:py-24 print-doc">
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="container-narrow max-w-3xl print-doc"
          data-testid="verhuurvoorwaarden-content"
        >
          <div className="bg-beige p-6 md:p-8 border-l-2 border-gold text-[15px] text-ink/80 leading-relaxed">
            <p className="font-serif text-lg text-ink mb-2">Lenslopers Fotografie</p>
            <p>Lemmens, Eric</p>
            <p>Ondernemingsnummer: BE0807.873.408</p>
            <p>
              E-mail:{" "}
              <a
                href={mailtoLink("Vraag verhuurvoorwaarden Magic Mirror")}
                className="text-gold hover:underline"
              >
                {BRAND.email}
              </a>
            </p>
          </div>

          <H2>1. Reservatie en betaling</H2>
          <UL>
            <li>Een reservatie is definitief na betaling van een voorschot van €150.</li>
            <li>
              Het voorschot dient binnen 7 dagen na goedkeuring van de aanvraag betaald te zijn om
              de datum te behouden.
            </li>
            <li>Het resterende bedrag dient op de dag van het evenement betaald te zijn.</li>
            <li>De schadewaarborg bedraagt €300.</li>
          </UL>

          <H2>2. Waarborg en schade</H2>
          <UL>
            <li>De waarborg van €300 staat los van de huurprijs en het voorschot.</li>
            <li>
              De huurder is gedurende de volledige huurperiode verantwoordelijk voor de Magic
              Mirror, printer, accessoires en overige ter beschikking gestelde materialen.
            </li>
            <li>
              De huurder is aansprakelijk voor schade, verlies of diefstal veroorzaakt door de
              huurder, gasten of andere aanwezigen.
            </li>
            <li>
              Bij schade worden de werkelijke herstel- of vervangingskosten met de waarborg
              verrekend.
            </li>
            <li>
              Indien de schade hoger is dan de waarborg, wordt het resterende bedrag afzonderlijk
              aan de huurder aangerekend.
            </li>
            <li>Normale slijtage en normale gebruikssporen worden niet als schade beschouwd.</li>
            <li>
              Indien er geen schade, verlies of ontbrekende materialen worden vastgesteld, wordt de
              waarborg volledig terugbetaald.
            </li>
          </UL>

          <H2>3. Gebruik van de Magic Mirror</H2>
          <UL>
            <li>
              De Magic Mirror mag uitsluitend worden gebruikt tijdens de afgesproken huurperiode.
            </li>
            <li>De booth mag niet worden verplaatst zonder toestemming van Lenslopers.</li>
            <li>De huurder zorgt voor een veilige, droge en geschikte opstelplaats.</li>
            <li>Kinderen gebruiken de Magic Mirror onder toezicht van een volwassene.</li>
            <li>
              De apparatuur mag niet worden blootgesteld aan regen, vocht, rook, confetti,
              vloeistoffen of andere omstandigheden die schade kunnen veroorzaken.
            </li>
          </UL>

          <H2>4. Stroomvoorziening</H2>
          <UL>
            <li>
              De huurder voorziet een geschikte 230V-stroomvoorziening binnen maximaal 5 meter van
              de opstelplaats.
            </li>
            <li>De stroomvoorziening dient veilig en betrouwbaar te zijn.</li>
          </UL>

          <H2>5. Opbouw en afbraak</H2>
          <UL>
            <li>Lenslopers verzorgt de opbouw en afbraak van de Magic Mirror.</li>
            <li>De opbouw gebeurt ongeveer 45 minuten vóór aanvang van de huurperiode.</li>
            <li>
              Na afloop moet de booth vrij toegankelijk zijn zodat deze veilig kan worden afgebroken
              en meegenomen.
            </li>
          </UL>

          <H2>6. Foto's en gebruik ervan</H2>
          <UL>
            <li>
              De gemaakte foto's mogen door Lenslopers worden gebruikt voor portfolio, website,
              sociale media en andere promotionele doeleinden, tenzij de huurder vooraf uitdrukkelijk
              aangeeft dat dit niet gewenst is.
            </li>
            <li>
              Wanneer de huurder geen toestemming geeft voor promotioneel gebruik, worden de foto's
              niet voor deze doeleinden gebruikt.
            </li>
          </UL>

          <H2>7. Technische problemen</H2>
          <UL>
            <li>
              Lenslopers zorgt ervoor dat de apparatuur vóór gebruik wordt gecontroleerd en correct
              wordt opgesteld.
            </li>
            <li>
              Bij technische problemen wordt alles in het werk gesteld om deze zo snel mogelijk op te
              lossen.
            </li>
            <li>
              Lenslopers kan niet aansprakelijk worden gesteld voor technische problemen die het
              gevolg zijn van omstandigheden buiten haar controle, zoals stroomuitval,
              internetproblemen, externe apparatuur of onoordeelkundig gebruik.
            </li>
          </UL>

          <H2>8. Annulatie</H2>
          <UL>
            <li>Bij annulatie door de huurder wordt het betaalde voorschot niet terugbetaald.</li>
            <li>
              Bij annulatie door Lenslopers wordt het reeds betaalde bedrag terugbetaald, tenzij in
              overleg een andere datum wordt vastgelegd.
            </li>
          </UL>

          <H2>9. Verplaatsing en transport</H2>
          <UL>
            <li>Verplaatsing is gratis tot 10 km rond Kieldrecht.</li>
            <li>Boven 10 km wordt een kilometervergoeding van €0,45 per km aangerekend.</li>
            <li>De kilometervergoeding wordt berekend voor de volledige heen- en terugrit.</li>
          </UL>

          <H2>10. Aansprakelijkheid</H2>
          <UL>
            <li>
              De huurder zorgt ervoor dat gasten de Magic Mirror op een correcte en zorgvuldige
              manier gebruiken.
            </li>
            <li>
              Lenslopers is niet aansprakelijk voor lichamelijke schade of schade aan persoonlijke
              eigendommen van gasten die ontstaat door het gebruik van de booth, behoudens gevallen
              waarin Lenslopers hiervoor wettelijk aansprakelijk is.
            </li>
          </UL>

          <H2>11. Akkoord met de voorwaarden</H2>
          <div className="bg-ink text-white p-8 md:p-10 relative mt-2">
            <span className="absolute top-0 left-0 w-10 h-px bg-gold" />
            <span className="absolute top-0 left-0 h-10 w-px bg-gold" />
            <span className="absolute bottom-0 right-0 w-10 h-px bg-gold" />
            <span className="absolute bottom-0 right-0 h-10 w-px bg-gold" />
            <p className="text-white/80 text-[15px] leading-relaxed">
              Door een reservatie te bevestigen en het voorschot te betalen, verklaart de huurder
              zich akkoord met bovenstaande verhuurvoorwaarden.
            </p>
          </div>

          <P>
            Deze verhuurvoorwaarden vormen een aanvulling op de{" "}
            <Link to="/voorwaarden" className="text-gold hover:underline">
              algemene voorwaarden en het privacybeleid
            </Link>{" "}
            van Lenslopers Fotografie.
          </P>

          <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-6 print-hide">
            <Link
              to="/magic-mirror"
              data-testid="verhuurvoorwaarden-back-link"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink/60 hover:text-gold transition-colors"
            >
              <ArrowLeft size={14} />
              Terug naar Magic Mirror
            </Link>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="verhuurvoorwaarden-print-btn-bottom"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink/60 hover:text-gold transition-colors sm:ml-auto"
            >
              <Printer size={14} />
              Download / Print als PDF
            </button>
          </div>
        </motion.article>
      </section>
    </>
  );
};

export default VerhuurvoorwaardenMagicMirror;
